import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";

export type TBookingAnchor = "START" | "END" | "FREE";

export interface ICronofyStatus {
  isConnected: boolean;
  email: string | null;
  hideEmailFromVisitors?: boolean;
  visitBookingAnchorDefault?: TBookingAnchor;
  // Missing on an API older than DEV-3354 reads as on, like the API does.
  visitMailsEnabled?: boolean;
}

/**
 * Reading the connection is `status`; writing it is `connection`.
 *
 * The two are not symmetric, and guessing that the read matched the write
 * cost us a feature that failed silently: the employee default was fetched
 * from a route that does not exist, every call fell into the catch, and a
 * new block simply never prefilled. Both paths live here so the next caller
 * cannot invent a third spelling (DEV-3328).
 */
export const CRONOFY_STATUS_PATH = "/cronofy/status";
export const CRONOFY_CONNECTION_PATH = "/cronofy/connection";

/** The shape both the read and the save answer with, at either nesting. */
export const readCronofyStatus = (response: {
  data?: { data?: ICronofyStatus } | ICronofyStatus;
}): ICronofyStatus | undefined =>
  (response?.data as { data?: ICronofyStatus })?.data ||
  (response?.data as ICronofyStatus) ||
  undefined;

export const getCronofyStatus = async () => {
  const response = await api.get<{ data?: ICronofyStatus }>(
    CRONOFY_STATUS_PATH,
    { isV2: true },
  );

  return readCronofyStatus(response);
};

export const BOOKING_ANCHORS: TBookingAnchor[] = ["START", "END", "FREE"];

export const BOOKING_ANCHOR_KEY: { [anchor in TBookingAnchor]: string } = {
  START: "rentmatch.calendar.booking_anchor.start",
  END: "rentmatch.calendar.booking_anchor.end",
  FREE: "rentmatch.calendar.booking_anchor.free",
};

/**
 * What an already saved block shows.
 *
 * A block saved before this setting existed has no value, and what it did was
 * offer every free moment. It is shown as that rather than as a choice nobody
 * made, so reading the list never rewrites what a block actually does.
 */
export const resolveSavedBlockAnchor = (
  saved?: string | null,
): TBookingAnchor => (saved as TBookingAnchor) || "FREE";

/**
 * What a block drawn right now starts on.
 *
 * The employee's own default, and START where they have none: a new block is
 * a choice being made today, so it follows the employee rather than the
 * backwards-compatible FREE that saved blocks fall back to (Jonas,
 * 2026-10-06).
 */
export const resolveNewBlockAnchor = (
  employeeDefault?: TBookingAnchor | null,
): TBookingAnchor => employeeDefault || "START";
